import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { BLOCK_DEFAULTS, SAMPLE_CONTACT, renderBlocksHtml, wrapPreview, uid } from "@/lib/emailBlocks";
import EditablePreview from "@/components/EditablePreview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Heading1, AlignLeft, Image as ImageIcon, Images, MousePointerClick, Minus, MoveVertical, Trash2, ChevronUp, ChevronDown, Monitor, Smartphone, Send, Save, Upload, Clock, LayoutGrid, Pencil, Eye, Plus, X, Code } from "lucide-react";

const BLOCK_TYPES = [
  { type: "heading", label: "Heading", icon: Heading1 },
  { type: "text", label: "Text", icon: AlignLeft },
  { type: "image", label: "Image", icon: ImageIcon },
  { type: "images", label: "Image Row", icon: Images },
  { type: "button", label: "Button", icon: MousePointerClick },
  { type: "cards", label: "Products", icon: LayoutGrid },
  { type: "html", label: "HTML", icon: Code },
  { type: "divider", label: "Divider", icon: Minus },
  { type: "spacer", label: "Spacer", icon: MoveVertical },
];

const inputCls = "bg-slate-900 border-slate-700 text-slate-100";
const TAGS = ["{{name}}", "{{email}}", "{{phone}}", "{{product_name}}", "{{city}}", "{{pin_code}}", "{{date}}"];

function BlockSettings({ block, onChange }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (block.type === "cards" && uploadTarget !== null) {
        onChange({ items: block.items.map((it, i) => (i === uploadTarget ? { ...it, image: data.url } : it)) });
      } else if (block.type === "images" && uploadTarget !== null) {
        onChange({ items: block.items.map((it, i) => (i === uploadTarget ? { ...it, url: data.url } : it)) });
      } else {
        onChange({ url: data.url });
      }
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setUploading(false);
      setUploadTarget(null);
    }
  };

  if (block.type === "heading" || block.type === "text")
    return (
      <div className="space-y-2">
        <Textarea data-testid="block-text-input" value={block.text} onChange={(e) => onChange({ text: e.target.value })} rows={3} className={inputCls} />
        <div className="flex gap-2">
          {block.type === "heading" && (
            <Input data-testid="block-size-input" type="number" value={block.size} onChange={(e) => onChange({ size: parseInt(e.target.value) || 24 })} className={`${inputCls} w-20 h-8 text-xs`} />
          )}
          <Select value={block.align} onValueChange={(v) => onChange({ align: v })}>
            <SelectTrigger className={`${inputCls} h-8 text-xs w-28`} data-testid="block-align-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
              {["left", "center", "right"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    );
  if (block.type === "image")
    return (
      <div className="space-y-2">
        <Input data-testid="block-image-url-input" value={block.url} onChange={(e) => onChange({ url: e.target.value })} placeholder="Image URL" className={`${inputCls} h-8 text-xs`} />
        <Input data-testid="block-image-link-input" value={block.link || ""} onChange={(e) => onChange({ link: e.target.value })} placeholder="Link URL — opens when image is clicked" className={`${inputCls} h-8 text-xs`} />
        <Input data-testid="block-image-width-input" type="number" value={block.width || ""} onChange={(e) => onChange({ width: e.target.value ? parseInt(e.target.value) : undefined })} placeholder="Width px (blank = full)" className={`${inputCls} h-8 text-xs w-40`} />
        <Button size="sm" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} className="border-slate-700 text-slate-300 h-8 w-full" data-testid="block-image-upload-button">
          <Upload size={13} className="mr-1.5" /> {uploading ? "Uploading…" : "Upload image"}
        </Button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} />
      </div>
    );
  if (block.type === "button")
    return (
      <div className="space-y-2">
        <Input data-testid="block-button-text-input" value={block.text} onChange={(e) => onChange({ text: e.target.value })} placeholder="Button text" className={`${inputCls} h-8 text-xs`} />
        <Input data-testid="block-button-url-input" value={block.url} onChange={(e) => onChange({ url: e.target.value })} placeholder="https://…" className={`${inputCls} h-8 text-xs`} />
        <div className="flex gap-2 items-center">
          <input data-testid="block-button-color-input" type="color" value={block.color} onChange={(e) => onChange({ color: e.target.value })} className="w-9 h-8 rounded bg-slate-900 border border-slate-700 cursor-pointer" />
          <Select value={block.align} onValueChange={(v) => onChange({ align: v })}>
            <SelectTrigger className={`${inputCls} h-8 text-xs flex-1`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
              {["left", "center", "right"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    );
  if (block.type === "spacer")
    return <Input data-testid="block-spacer-height-input" type="number" value={block.height} onChange={(e) => onChange({ height: parseInt(e.target.value) || 24 })} className={`${inputCls} h-8 text-xs w-24`} />;
  if (block.type === "html")
    return (
      <div className="space-y-1">
        <Textarea data-testid="block-html-input" value={block.html || ""} onChange={(e) => onChange({ html: e.target.value })} rows={8} spellCheck={false} placeholder="<table>…</table> — paste any email-safe HTML" className={`${inputCls} font-mono text-[11px] leading-relaxed`} />
        <p className="text-[10px] text-slate-500">Inserted as-is into the email. Use inline styles and tables for best email-client support. Personalization tags work here too.</p>
      </div>
    );
  if (block.type === "images") {
    const items = block.items || [];
    const updItem = (i, patch) => onChange({ items: items.map((it, j) => (j === i ? { ...it, ...patch } : it)) });
    return (
      <div className="space-y-2">
        <p className="text-[10px] text-slate-500">Side-by-side images, each with its own link. Slice a banner into parts to make regions clickable.</p>
        {items.map((it, i) => (
          <div key={i} className="p-2 rounded-lg border border-slate-800 bg-slate-950/50 space-y-1.5" data-testid="image-row-item-editor">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono uppercase text-slate-500">Image {i + 1}</span>
              <button data-testid="image-row-item-remove" onClick={() => onChange({ items: items.filter((_, j) => j !== i) })} className="text-slate-500 hover:text-rose-400"><X size={12} /></button>
            </div>
            <div className="flex gap-1.5">
              <Input data-testid="image-row-url-input" value={it.url} onChange={(e) => updItem(i, { url: e.target.value })} placeholder="Image URL" className={`${inputCls} h-7 text-[11px]`} />
              <Button size="sm" variant="outline" disabled={uploading} onClick={() => { setUploadTarget(i); fileRef.current?.click(); }} className="border-slate-700 text-slate-300 h-7 px-2 shrink-0" data-testid="image-row-upload-button">
                <Upload size={11} />
              </Button>
            </div>
            <Input data-testid="image-row-link-input" value={it.link || ""} onChange={(e) => updItem(i, { link: e.target.value })} placeholder="Link URL for this image" className={`${inputCls} h-7 text-[11px]`} />
          </div>
        ))}
        {items.length < 4 && (
          <Button data-testid="image-row-add" size="sm" variant="outline" onClick={() => onChange({ items: [...items, { url: "", link: "", alt: "" }] })} className="border-dashed border-slate-700 text-slate-300 h-7 w-full">
            <Plus size={12} className="mr-1" /> Add image (max 4)
          </Button>
        )}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} />
      </div>
    );
  }
  if (block.type === "cards") {
    const items = block.items || [];
    const updItem = (i, patch) => onChange({ items: items.map((it, j) => (j === i ? { ...it, ...patch } : it)) });
    return (
      <div className="space-y-3">
        <div className="flex gap-2 items-center">
          <Select value={String(block.columns || 2)} onValueChange={(v) => onChange({ columns: parseInt(v) })}>
            <SelectTrigger data-testid="cards-columns-select" className={`${inputCls} h-8 text-xs w-28`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
              <SelectItem value="2">2 columns</SelectItem>
              <SelectItem value="3">3 columns</SelectItem>
            </SelectContent>
          </Select>
          <input data-testid="cards-color-input" type="color" value={block.color || "#6366F1"} onChange={(e) => onChange({ color: e.target.value })} className="w-9 h-8 rounded bg-slate-900 border border-slate-700 cursor-pointer" title="Button color" />
        </div>
        {items.map((it, i) => (
          <div key={i} className="p-2 rounded-lg border border-slate-800 bg-slate-950/50 space-y-1.5" data-testid="card-item-editor">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono uppercase text-slate-500">Card {i + 1}</span>
              <button data-testid="card-item-remove" onClick={() => onChange({ items: items.filter((_, j) => j !== i) })} className="text-slate-500 hover:text-rose-400"><X size={12} /></button>
            </div>
            <div className="flex gap-1.5">
              <Input data-testid="card-item-image-input" value={it.image} onChange={(e) => updItem(i, { image: e.target.value })} placeholder="Image URL" className={`${inputCls} h-7 text-[11px]`} />
              <Button size="sm" variant="outline" disabled={uploading} onClick={() => { setUploadTarget(i); fileRef.current?.click(); }} className="border-slate-700 text-slate-300 h-7 px-2 shrink-0" data-testid="card-item-upload-button">
                <Upload size={11} />
              </Button>
            </div>
            <Input data-testid="card-item-title-input" value={it.title} onChange={(e) => updItem(i, { title: e.target.value })} placeholder="Title" className={`${inputCls} h-7 text-[11px]`} />
            <div className="flex gap-1.5">
              <Input data-testid="card-item-price-input" value={it.price} onChange={(e) => updItem(i, { price: e.target.value })} placeholder="Price (blank = category)" className={`${inputCls} h-7 text-[11px]`} />
              <Input data-testid="card-item-old-price-input" value={it.old_price} onChange={(e) => updItem(i, { old_price: e.target.value })} placeholder="Old price" className={`${inputCls} h-7 text-[11px]`} />
            </div>
            <div className="flex gap-1.5">
              <Input data-testid="card-item-button-input" value={it.button} onChange={(e) => updItem(i, { button: e.target.value })} placeholder="Button label" className={`${inputCls} h-7 text-[11px]`} />
              <Input data-testid="card-item-url-input" value={it.url} onChange={(e) => updItem(i, { url: e.target.value })} placeholder="Link URL" className={`${inputCls} h-7 text-[11px]`} />
            </div>
          </div>
        ))}
        <Button data-testid="card-item-add" size="sm" variant="outline" onClick={() => onChange({ items: [...items, { image: "", title: "Product name", price: "", old_price: "", url: "https://example.com", button: "Shop Now" }] })} className="border-dashed border-slate-700 text-slate-300 h-7 w-full">
          <Plus size={12} className="mr-1" /> Add card
        </Button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} />
      </div>
    );
  }
  return null;
}

export default function EmailCampaignNew() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [blocks, setBlocks] = useState([
    { id: uid(), ...BLOCK_DEFAULTS.heading },
    { id: uid(), ...BLOCK_DEFAULTS.text },
    { id: uid(), ...BLOCK_DEFAULTS.button },
  ]);
  const [selected, setSelected] = useState(null);
  const [audiences, setAudiences] = useState([]);
  const [audienceId, setAudienceId] = useState("");
  const [recipientCount, setRecipientCount] = useState(null);
  const [device, setDevice] = useState("desktop");
  const [canvasMode, setCanvasMode] = useState("edit");
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [sendMode, setSendMode] = useState("now");
  const [scheduleAt, setScheduleAt] = useState("");
  const [footer, setFooter] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/audiences").then((r) => setAudiences(r.data)).catch(() => {});
    api.get("/settings").then((r) => setFooter(r.data.footer || null)).catch(() => {});
    api.get("/templates").then((r) => {
      setTemplates(r.data);
      const tid = params.get("template");
      if (tid) {
        const t = r.data.find((x) => x.id === tid);
        if (t) loadTemplate(t);
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!audienceId) return setRecipientCount(null);
    api.post("/email-campaigns/recipient-count", { audience_id: audienceId }).then((r) => setRecipientCount(r.data.email)).catch(() => {});
  }, [audienceId]);

  useEffect(() => {
    if (user?.email && !testTo) setTestTo(user.email);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendTest = async () => {
    if (!testTo.trim()) return toast.error("Enter an email for the test");
    if (!subject.trim()) return toast.error("Add a subject line first");
    if (!blocks.length) return toast.error("Add at least one content block");
    setTesting(true);
    try {
      await api.post("/email-campaigns/test-send", { subject, blocks: blocks.map(({ id, ...b }) => b), to_email: testTo.trim() });
      toast.success(`Test email sent to ${testTo.trim()}`);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setTesting(false);
    }
  };

  const loadTemplate = (t) => {
    setBlocks((t.blocks || []).map((b) => ({ ...b, id: uid() })));
    if (t.subject) setSubject(t.subject);
    toast.success(`Loaded template "${t.name}"`);
  };

  const addBlock = (type) => {
    const b = { id: uid(), ...BLOCK_DEFAULTS[type] };
    setBlocks([...blocks, b]);
    setSelected(b.id);
  };
  const updateBlock = (id, patch) => setBlocks(blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const updateCardItem = (id, idx, patch) =>
    setBlocks(blocks.map((b) => (b.id === id ? { ...b, items: b.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) } : b)));

  const reorderBlocks = (dragId, targetId) => {
    setBlocks((prev) => {
      const from = prev.findIndex((b) => b.id === dragId);
      const to = prev.findIndex((b) => b.id === targetId);
      if (from < 0 || to < 0) return prev;
      const copy = [...prev];
      const [moved] = copy.splice(from, 1);
      copy.splice(to, 0, moved);
      return copy;
    });
  };

  const handleDropFiles = async (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/")).slice(0, 6);
    if (!files.length) return toast.error("Drop image files only");
    toast.info(`Uploading ${files.length} image${files.length > 1 ? "s" : ""}…`);
    const newBlocks = [];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newBlocks.push({ id: uid(), ...BLOCK_DEFAULTS.image, url: data.url });
      } catch (e) {
        toast.error(`${f.name}: ${formatErr(e)}`);
      }
    }
    if (newBlocks.length) {
      setBlocks((prev) => [...prev, ...newBlocks]);
      setSelected(newBlocks[newBlocks.length - 1].id);
      toast.success(`Added ${newBlocks.length} image block${newBlocks.length > 1 ? "s" : ""}`);
    }
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const copy = [...blocks];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setBlocks(copy);
  };

  const saveTemplate = async () => {
    const tname = window.prompt("Template name?");
    if (!tname) return;
    try {
      await api.post("/templates", { name: tname, subject, blocks: blocks.map(({ id, ...b }) => b) });
      toast.success("Saved as template");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const send = async () => {
    if (!name.trim()) return toast.error("Give the campaign a name");
    if (!subject.trim()) return toast.error("Add a subject line");
    if (!audienceId) return toast.error("Pick an audience");
    if (!blocks.length) return toast.error("Add at least one content block");
    let scheduled_at = null;
    if (sendMode === "schedule") {
      if (!scheduleAt) return toast.error("Pick a date and time");
      const dt = new Date(scheduleAt);
      if (dt <= new Date()) return toast.error("Scheduled time must be in the future");
      scheduled_at = dt.toISOString();
    }
    setSending(true);
    try {
      await api.post("/email-campaigns", { name, subject, audience_id: audienceId, blocks: blocks.map(({ id, ...b }) => b), scheduled_at });
      toast.success(scheduled_at ? `Campaign scheduled for ${new Date(scheduled_at).toLocaleString()}` : "Campaign queued — sending in background");
      navigate("/email-campaigns");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSending(false);
    }
  };

  const previewHtml = wrapPreview(renderBlocksHtml(blocks, SAMPLE_CONTACT), footer);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button data-testid="campaign-back-button" onClick={() => navigate("/email-campaigns")} className="text-slate-400 hover:text-slate-100 p-1"><ArrowLeft size={19} /></button>
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-indigo-400 mb-0.5">Design → audience → send</p>
          <h1 className="font-heading text-2xl font-bold text-slate-50">New email campaign</h1>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 max-w-3xl">
        <Input data-testid="campaign-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Campaign name (internal)" className={inputCls} />
        <Input data-testid="campaign-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject line — supports {{name}} etc." className={inputCls} />
      </div>

      <div className="grid lg:grid-cols-[380px_1fr] gap-6 items-start">
        {/* Left: blocks */}
        <div className="space-y-4">
          <div className="bg-[#111827] border border-slate-800 rounded-xl p-4">
            <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3">Add block</p>
            <div className="grid grid-cols-3 gap-2">
              {BLOCK_TYPES.map(({ type, label, icon: Icon }) => (
                <button key={type} data-testid={`email-block-${type}`} onClick={() => addBlock(type)} className="flex flex-col items-center gap-1 p-3 rounded-lg bg-slate-900/70 border border-slate-800 hover:border-indigo-500/50 text-slate-300 text-xs transition-colors">
                  <Icon size={15} /> {label}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-[#111827] border border-slate-800 rounded-xl p-4 space-y-2">
            <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Blocks</p>
            {blocks.map((b, i) => (
              <div key={b.id} data-testid="editor-block-row" className={`rounded-lg border p-3 cursor-pointer transition-colors ${selected === b.id ? "border-indigo-500/60 bg-indigo-500/5" : "border-slate-800 bg-slate-900/50 hover:border-slate-700"}`} onClick={() => setSelected(selected === b.id ? null : b.id)}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-300 capitalize">{b.type}</span>
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <button data-testid="block-move-up" onClick={() => move(i, -1)} className="text-slate-500 hover:text-slate-200 p-0.5"><ChevronUp size={13} /></button>
                    <button data-testid="block-move-down" onClick={() => move(i, 1)} className="text-slate-500 hover:text-slate-200 p-0.5"><ChevronDown size={13} /></button>
                    <button data-testid="block-delete" onClick={() => setBlocks(blocks.filter((x) => x.id !== b.id))} className="text-slate-500 hover:text-rose-400 p-0.5"><Trash2 size={13} /></button>
                  </div>
                </div>
                {selected === b.id && (
                  <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                    <BlockSettings block={b} onChange={(patch) => updateBlock(b.id, patch)} />
                  </div>
                )}
              </div>
            ))}
            <div className="pt-1">
              <p className="text-[10px] text-slate-500 mb-1">Personalization tags:</p>
              <div className="flex flex-wrap gap-1">
                {TAGS.map((t) => <code key={t} className="text-[10px] font-mono bg-slate-800 text-indigo-300 px-1.5 py-0.5 rounded">{t}</code>)}
              </div>
            </div>
          </div>

          <div className="bg-[#111827] border border-slate-800 rounded-xl p-4 space-y-3">
            <p className="text-xs font-mono uppercase tracking-widest text-slate-500">Audience & send</p>
            {templates.length > 0 && (
              <Select onValueChange={(v) => { const t = templates.find((x) => x.id === v); if (t) loadTemplate(t); }}>
                <SelectTrigger data-testid="load-template-select" className={`${inputCls} h-9 text-sm`}><SelectValue placeholder="Load a template…" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={audienceId} onValueChange={setAudienceId}>
              <SelectTrigger data-testid="campaign-audience-select" className={`${inputCls} h-9 text-sm`}><SelectValue placeholder="Choose audience…" /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                {audiences.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.count})</SelectItem>)}
              </SelectContent>
            </Select>
            {recipientCount !== null && (
              <p data-testid="campaign-recipient-count" className="text-xs font-mono text-emerald-400">
                {recipientCount} unique recipients after dedup & unsubscribes
              </p>
            )}
            <div className="pt-1 border-t border-slate-800 space-y-2">
              <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Test before launch</p>
              <div className="flex gap-2">
                <Input data-testid="test-send-email-input" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@inbox.com" className={`${inputCls} h-9 text-sm`} />
                <Button data-testid="test-send-button" variant="outline" onClick={sendTest} disabled={testing} className="border-slate-700 text-slate-200 hover:bg-slate-800 shrink-0 h-9">
                  <Send size={13} className="mr-1.5" /> {testing ? "Sending…" : "Send test"}
                </Button>
              </div>
            </div>
            <div className="flex rounded-lg border border-slate-700 overflow-hidden">
              <button data-testid="send-mode-now" onClick={() => setSendMode("now")} className={`flex-1 py-2 text-xs font-medium transition-colors ${sendMode === "now" ? "bg-indigo-500/20 text-indigo-300" : "text-slate-400 hover:text-slate-200"}`}>
                Send now
              </button>
              <button data-testid="send-mode-schedule" onClick={() => setSendMode("schedule")} className={`flex-1 py-2 text-xs font-medium transition-colors ${sendMode === "schedule" ? "bg-indigo-500/20 text-indigo-300" : "text-slate-400 hover:text-slate-200"}`}>
                Schedule
              </button>
            </div>
            {sendMode === "schedule" && (
              <div className="space-y-1">
                <Input data-testid="schedule-datetime-input" type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} className={`${inputCls} h-9 text-sm`} />
                <p className="text-[10px] text-slate-500">Your local time. Dispatched within ~15 minutes of the scheduled time.</p>
              </div>
            )}
            <div className="flex gap-2">
              <Button data-testid="save-template-button" variant="outline" onClick={saveTemplate} className="border-slate-700 text-slate-300 hover:bg-slate-800 flex-1">
                <Save size={14} className="mr-1.5" /> Save template
              </Button>
              <Button data-testid="send-campaign-button" onClick={send} disabled={sending} className="bg-indigo-500 hover:bg-indigo-600 text-white flex-1 font-semibold">
                {sendMode === "schedule" ? <Clock size={14} className="mr-1.5" /> : <Send size={14} className="mr-1.5" />}
                {sending ? "Queuing…" : sendMode === "schedule" ? "Schedule" : "Send"}
              </Button>
            </div>
          </div>
        </div>

        {/* Right: preview */}
        <div className="bg-[#111827] border border-slate-800 rounded-xl overflow-hidden sticky top-4">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
            <div className="flex items-center gap-2">
              <div className="flex rounded-lg border border-slate-700 overflow-hidden">
                <button data-testid="canvas-mode-edit" onClick={() => setCanvasMode("edit")} className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-colors ${canvasMode === "edit" ? "bg-indigo-500/20 text-indigo-300" : "text-slate-500 hover:text-slate-300"}`}>
                  <Pencil size={12} /> Edit
                </button>
                <button data-testid="canvas-mode-preview" onClick={() => setCanvasMode("preview")} className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-colors ${canvasMode === "preview" ? "bg-indigo-500/20 text-indigo-300" : "text-slate-500 hover:text-slate-300"}`}>
                  <Eye size={12} /> Preview
                </button>
              </div>
              <span className="hidden xl:inline text-[10px] font-mono uppercase tracking-widest text-slate-600">
                {canvasMode === "edit" ? "click any text to edit in place" : `sample: ${SAMPLE_CONTACT.name}`}
              </span>
            </div>
            <div className="flex gap-1">
              <button data-testid="preview-desktop-toggle" onClick={() => setDevice("desktop")} className={`p-1.5 rounded ${device === "desktop" ? "bg-indigo-500/20 text-indigo-300" : "text-slate-500 hover:text-slate-300"}`}><Monitor size={15} /></button>
              <button data-testid="preview-mobile-toggle" onClick={() => setDevice("mobile")} className={`p-1.5 rounded ${device === "mobile" ? "bg-indigo-500/20 text-indigo-300" : "text-slate-500 hover:text-slate-300"}`}><Smartphone size={15} /></button>
            </div>
          </div>
          <div className="p-4 bg-slate-950/60 overflow-x-auto">
            <div className="mx-auto transition-all duration-300" style={{ maxWidth: device === "desktop" ? 640 : 375 }}>
              {canvasMode === "edit" ? (
                <EditablePreview blocks={blocks} selected={selected} setSelected={setSelected} updateBlock={updateBlock} updateCardItem={updateCardItem} footer={footer} onReorder={reorderBlocks} onDropFiles={handleDropFiles} />
              ) : (
                <div data-testid="email-preview-panel" dangerouslySetInnerHTML={{ __html: previewHtml }} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
